import React, { Component } from "react";
import PropTypes from "prop-types";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { parse } from "query-string";
import { UsaStates } from "usa-states";
import TextInput from "../../../../Components/molecules/forms/inputs/TextInput";
import EmailInput from "../../../../Components/molecules/forms/inputs/EmailInput";
import DateInput from "../../../../Components/molecules/forms/inputs/DateInput";
import PasswordInput from "../../../../Components/molecules/forms/inputs/PasswordInput";
import SelectInput from "../../../../Components/molecules/forms/inputs/SelectInput";
import CheckboxInput from "../../../../Components/molecules/forms/inputs/CheckboxInput";
import ReCAPTCHAInput from "../../../../Components/molecules/forms/inputs/ReCAPTCHAInput";
import {
  password as passwordSchema,
  passwordConfirm as passwordConfirmSchema
} from "../../../../Helpers/Schema";
import { gridClasses } from "../../../../Helpers/GridClasses";
import drupalSettings from "../../../../Helpers/drupalSettings";
import Message from "../../../../Components/molecules/message/Message";
import { isLegalDrinkingAge } from "../../../../Helpers/DateHelper";

const lang = drupalSettings.path.currentLanguage;
const reCaptchaKey = drupalSettings.ed_dashboard.recaptcha_site_key;
const brandName = drupalSettings.ed_dashboard.brand_name;
const isTGR = brandName === "The Glenrothes";
const privacyLink = drupalSettings.ed_dashboard.privacy_link;
const termsAndConditionsLink =
  drupalSettings.ed_dashboard.terms_and_conditions_link;
const mandatoryPrivacyPolicyOptInMarkets = Object.keys(
  drupalSettings.ed_dashboard.mandatory_privacy_opt_in_markets
    ? drupalSettings.ed_dashboard.mandatory_privacy_opt_in_markets
    : {}
);
const marketingEnabled = drupalSettings.ed_dashboard.marketing_enabled;
const countryList = Object.entries(drupalSettings.ed_dashboard.country_list)
  .map(([key, value]) => ({ key, value }))
  .sort((countryA, countryB) => countryA.value.localeCompare(countryB.value));
const usStates = new UsaStates();
const statesNames = usStates.format({
  $key: "abbreviation",
  $value: "name"
});

class SignUpStep1 extends Component {
  constructor(props) {
    super(props);
    this.handleSubmit = this.handleSubmit.bind(this);

    const { location } = this.props;
    const parameters = parse(location.search);

    this.state = {
      message: "",
      parameters,
      dobValidation: {
        invalid: false,
        incomplete: false
      }
    };
  }

  /**
   * Function that receives a Date object
   * and returns the date in the format YYYY-MM-DD
   * @param {Date} date the date to formate
   * @returns {String} the formated date
   */
  formatDate(date) {
    const year = date.getFullYear();
    let month = date.getMonth() + 1;
    let day = date.getDate();

    // Pad month and day
    if (month < 10) month = `0${month}`;
    if (day < 10) day = `0${day}`;

    return `${year}-${month}-${day}`;
  }

  handleSubmit(values, actions) {
    if (!isLegalDrinkingAge(values.dob)) {
      this.setState({
        message: (
          <Message type="error">
            {window.Drupal.t(
              "You must be of the legal drinking age in your country"
            )}
          </Message>
        )
      });
    } else {
      const {
        givenName,
        familyName,
        email,
        dob,
        country,
        city,
        stateProvince,
        password,
        marketingOptIn
      } = values;

      const { client, callback, nextStep } = this.props;

      const { parameters } = this.state;

      const formattedDob = this.formatDate(new Date(dob));

      const trackingData = {
        medium: parameters.utm_medium || null,
        campaign: parameters.utm_campaign || null,
        source: parameters.utm_source || null
      };

      let marketingAccepted = true;

      if (marketingEnabled) {
        marketingAccepted =
          marketingOptIn.length > 0 && marketingOptIn[0] === "1";
      }

      const postData = {
        givenName,
        familyName,
        dateOfBirth: formattedDob,
        email,
        password,
        location: country,
        nearestCity: country === "US" ? null : city,
        stateProvince: country === "US" ? stateProvince : null,
        trackingData,
        marketingOptIn: marketingAccepted,
        privacyPolicyOptIn: null
      };

      // Consumer API now handles auth provider sign up as well.
      client
        .post(`consumer/account`, postData)
        .then(() => {
          callback(nextStep);
        })
        .catch(() => {
          this.setState({
            message: (
              <Message type="error">
                {window.Drupal.t(
                  "A problem has occurred. Please try again later."
                )}
              </Message>
            )
          });
          return actions.setSubmitting(false);
        });
    }
  }

  setDobValidation = updatedVal => {
    this.setState({ dobValidation: updatedVal });
  };

  render() {
    const { message, parameters, dobValidation } = this.state;
    const { fullWidth } = this.props;
    const contentGridClass = fullWidth
      ? gridClasses.full
      : gridClasses.withoutSidebar;

    return (
      <div>
        <div className={gridClasses.row}>
          <div className={`${gridClasses.full} text-center`}>
            <h1>{window.Drupal.t("Sign Up")}</h1>
            {!isTGR ? (
              <p>
                {window.Drupal.t(
                  "By providing your information below, you are agreeing to create an account on their website and opting in to receiving marketing communications. These communications may include new product launches, upcoming events, experiences, partnerships, regional events, and distillery updates. \n" +
                    "Once your account is created, you can review and update the information you receive from @brandName at any time through the communications preference centre within your account dashboard.",
                  {
                    "@brandName": brandName
                  }
                )}
              </p>
            ) : (
              <div>
                <p>
                  {window.Drupal.t(
                    "By submitting your details below, you are agreeing to create an account on @brandName website and opting in to receive email marketing communications.  This may include information such as new partnerships, product releases, events, experiences, regional communications and distillery updates.",
                    {
                      "@brandName": brandName
                    }
                  )}
                </p>
                <p>
                  {window.Drupal.t(
                    "Once you have created an account, you can review and update what information you receive from @brandName at any time via the communications preference centre by logging into your account on our website",
                    {
                      "@brandName": brandName
                    }
                  )}
                </p>
              </div>
            )}
          </div>
        </div>

        {message && (
          <div className={gridClasses.row}>
            <div className={contentGridClass}>{message}</div>
          </div>
        )}

        <Formik
          initialValues={{
            givenName: parameters.givenName || "",
            familyName: parameters.familyName || "",
            email: parameters.email || "",
            dob: "",
            country:
              localStorage.getItem("deliveryLocation") ||
              drupalSettings.regionMarket.market ||
              drupalSettings.regionMarket.location,
            city: "",
            stateProvince: "",
            password: "",
            password_confirm: "",
            termsAndConditionsOptIn: [],
            marketingOptIn: [],
            privacyPolicyOptIn: []
          }}
          validationSchema={Yup.object().shape({
            givenName: Yup.string()
              .required(window.Drupal.t("First name is required"))
              .max("50", window.Drupal.t("First name is too long")),
            familyName: Yup.string()
              .required(window.Drupal.t("Last name is required"))
              .max("50", window.Drupal.t("Last name is too long")),
            email: Yup.string()
              .email(window.Drupal.t("Email is not valid"))
              .required(window.Drupal.t("Email is required"))
              .matches(/^[^+]*$/, window.Drupal.t("Email is not valid")),
            dob: Yup.string().required(() => {
              if (dobValidation.invalid) {
                return "Invalid date of birth";
              }
              if (dobValidation.incomplete) {
                return "Date of birth is required";
              }
              return "Date of birth is required";
            }),
            country: Yup.string().required(
              window.Drupal.t("Location is required")
            ),
            city: Yup.string()
              .max("100", window.Drupal.t("City is too long"))
              .when("country", {
                is: value => value !== "US",
                then: Yup.string().required(
                  window.Drupal.t(
                    "Please add your city so we can send you tailored communications"
                  )
                )
              }),
            password: passwordSchema,
            password_confirm: passwordConfirmSchema,
            privacyPolicyOptIn: Yup.array().test(
              "valid-privacy-opt-in",
              window.Drupal.t("Please accept the privacy policy to continue."),
              value => {
                if (
                  mandatoryPrivacyPolicyOptInMarkets.includes(
                    localStorage.getItem("deliveryLocation") ||
                      drupalSettings.regionMarket.market ||
                      drupalSettings.regionMarket.location
                  )
                ) {
                  return value[0] === "1";
                }
                return true;
              }
            ),
            reCAPTCHA: Yup.string()
              .nullable()
              .test(
                "recaptcha-required-if-enabled",
                window.Drupal.t("Please confirm you are not a robot."),
                value => {
                  if (reCaptchaKey) {
                    if (!value) {
                      return false;
                    }
                  }
                  return true;
                }
              ),
            marketingOptIn: Yup.string().when([], {
              is: () => marketingEnabled,
              then: Yup.string().required(
                window.Drupal.t(
                  "Please accept the marketing terms to continue."
                )
              )
            }),
            termsAndConditionsOptIn: Yup.string().when([], {
              is: () => !isTGR,
              then: Yup.string().required(
                window.Drupal.t(
                  "Please accept the terms & conditions to continue."
                )
              )
            })
          })}
          onSubmit={this.handleSubmit}
          render={props => (
            <Form>
              <div className={gridClasses.row}>
                <div className={contentGridClass}>
                  <TextInput
                    name="givenName"
                    label={window.Drupal.t("First name")}
                    required
                  />
                </div>
              </div>
              <div className={gridClasses.row}>
                <div className={contentGridClass}>
                  <TextInput
                    name="familyName"
                    label={window.Drupal.t("Last name")}
                    required
                  />
                </div>
              </div>
              <div className={gridClasses.row}>
                <div className={contentGridClass}>
                  <EmailInput
                    name="email"
                    label={window.Drupal.t("Email")}
                    required
                  />
                </div>
              </div>
              <div className={gridClasses.row}>
                <div className={contentGridClass}>
                  <DateInput
                    label={window.Drupal.t("Date of Birth")}
                    name="dob"
                    wrapperClass="form-item form-item__date-of-birth"
                    formikProps={props}
                    dateString=""
                    setDobValidation={this.setDobValidation}
                    required
                  />
                </div>
              </div>
              <div className={gridClasses.row}>
                <div className={contentGridClass}>
                  <SelectInput
                    name="country"
                    label={window.Drupal.t("Location of residence")}
                    options={countryList}
                    selectPlaceholder={window.Drupal.t("Select location")}
                    required
                  />
                </div>
              </div>

              {props.values.country !== "US" && (
                <div className={gridClasses.row}>
                  <div className={contentGridClass}>
                    <TextInput
                      name="city"
                      label={window.Drupal.t("City")}
                      required
                    />
                  </div>
                </div>
              )}
              {props.values.country === "US" && (
                <div className={gridClasses.row}>
                  <div className={contentGridClass}>
                    <SelectInput
                      name="stateProvince"
                      label={window.Drupal.t("State")}
                      options={statesNames}
                      selectPlaceholder={window.Drupal.t("Select state")}
                      required
                    />
                  </div>
                </div>
              )}

              <div className={gridClasses.row}>
                <div className={contentGridClass}>
                  <PasswordInput
                    name="password"
                    label={window.Drupal.t("Password")}
                    required
                    tooltip={window.Drupal.t(
                      "Passwords must be 8 characters or more, and contain upper and lowercase letters, numbers and one of the following symbols: !@#$%^&*"
                    )}
                  />
                </div>
              </div>
              <div className={gridClasses.row}>
                <div className={contentGridClass}>
                  <PasswordInput
                    name="password_confirm"
                    label={window.Drupal.t("Confirm Password")}
                    required
                  />
                </div>
              </div>
              {!isTGR && (
                <div className={gridClasses.row}>
                  <div className={contentGridClass}>
                    <p
                      dangerouslySetInnerHTML={{
                        __html: window.Drupal.t(
                          "By entering and submitting my information, I give my consent to @brandName to process my personal data in accordance with their  <a href='@privacyPolicyURL' target='_blank' rel='nofollow noreferrer'>Privacy Policy</a> and to receive email correspondence from them. You have the option to withdraw your consent at any time, as outlined in our <a href='@privacyPolicyURL' target='_blank' rel='nofollow noreferrer'>Privacy Policy</a> or by contacting us at <a href='@mailto' target='_blank' rel='nofollow noreferrer'>hello@highlandparkwhisky.com</a>.",
                          {
                            "@brandName": brandName,
                            "@privacyPolicyURL": `/${lang}${privacyLink}`,
                            "@mailto": "mailto:hello@highlandparkwhisky.com"
                          }
                        )
                      }}
                    />
                  </div>
                </div>
              )}

              {marketingEnabled && (
                <div className={gridClasses.row}>
                  <div className={contentGridClass}>
                    <CheckboxInput
                      name="marketingOptIn"
                      id="marketingOptIn"
                      label={
                        isTGR
                          ? window.Drupal.t(
                              "By entering and submitting my details, I consent to The Glenrothes processing my personal data in accordance with the <a href='@privacyPolicyURL' target='_blank' rel='nofollow noreferrer'>Privacy Policy</a> and receiving email communications from The Glenrothes.",
                              {
                                "@privacyPolicyURL": `/${lang}${privacyLink}`
                              }
                            )
                          : window.Drupal.t(
                              "By signing up to the Inner Circle you are creating an account and opting into receiving email communications from Highland Park and its regional entities, which include global and regional communications. These include brand campaigns, new product releases, events, and regional specific communications."
                            )
                      }
                      required
                      value="1"
                    />
                  </div>
                </div>
              )}

              {!isTGR && (
                <div className={gridClasses.row}>
                  <div className={contentGridClass}>
                    <CheckboxInput
                      name="termsAndConditionsOptIn"
                      id="termsAndConditionsOptIn"
                      label={window.Drupal.t(
                        "Please confirm you have read and accept the <a href='@termsAndConditionsURL' target='_blank' rel='nofollow noreferrer'>Terms & Conditions</a>.",
                        {
                          "@termsAndConditionsURL": `/${lang}${termsAndConditionsLink}`
                        }
                      )}
                      value="1"
                      required
                    />
                  </div>
                </div>
              )}

              {isTGR && (
                <div className={gridClasses.row}>
                  <div className={contentGridClass}>
                    <p
                      dangerouslySetInnerHTML={{
                        __html: window.Drupal.t(
                          "You can withdraw your consent at any time as set out in our <a href='@privacyPolicyURL' target='_blank' rel='nofollow noreferrer'>Privacy Policy</a> or by contacting us at <a href='@mailto' target='_blank' rel='nofollow noreferrer'>hello@theglenrothes.com</a>.",
                          {
                            "@privacyPolicyURL": `/${lang}${privacyLink}`,
                            "@mailto": "mailto:hello@theglenrothes.com"
                          }
                        )
                      }}
                    />
                  </div>
                </div>
              )}

              {mandatoryPrivacyPolicyOptInMarkets.includes(
                localStorage.getItem("deliveryLocation") ||
                  drupalSettings.regionMarket.market ||
                  drupalSettings.regionMarket.location
              ) && (
                <div className={gridClasses.row}>
                  <div className={contentGridClass}>
                    <CheckboxInput
                      name="privacyPolicyOptIn"
                      id="privacyPolicyOptIn"
                      label={window.Drupal.t(
                        "Please tick to confirm you have read and agree to the processing of your personal data as set out in our <a href='@privacyPolicyURL' target='_blank' rel='nofollow noreferrer'>Privacy Policy</a>.",
                        {
                          "@privacyPolicyURL": `/${lang}${privacyLink}`
                        }
                      )}
                      value="1"
                      required
                    />
                  </div>
                </div>
              )}

              <div className={gridClasses.row}>
                <div className={contentGridClass}>
                  <ReCAPTCHAInput
                    name="reCAPTCHA"
                    label=""
                    setFieldValue={props.setFieldValue}
                  />
                </div>
              </div>

              <div className={`signup-actions ${gridClasses.row}`}>
                <div className={gridClasses.halfWithoutSidebar}>
                  <button
                    className="button button--primary button--stretch"
                    type="submit"
                    disabled={props.isSubmitting || !props.isValid}
                  >
                    {isTGR
                      ? window.Drupal.t("Submit")
                      : window.Drupal.t("Confirm")}
                  </button>
                </div>
              </div>
            </Form>
          )}
        />
      </div>
    );
  }
}

SignUpStep1.propTypes = {
  client: PropTypes.func.isRequired,
  callback: PropTypes.func.isRequired,
  location: PropTypes.shape({
    search: PropTypes.string
  }).isRequired,
  fullWidth: PropTypes.bool
};

SignUpStep1.defaultProps = {
  fullWidth: false
};

export default SignUpStep1;
